// client/src/routes/app/properties/$propertyId/edit.tsx — SCR-PROP-002 (edit) · PROP-004.
//
// Doc 04's file layout (§9.2, line listing) puts the edit screen UNDER `$propertyId/`, so it
// renders inside the detail layout and keeps the property's header and identity chips in view
// while its fields are being changed. Same wizard as „proprietate nouă": one schema, one set of
// steps, one place to add a field.
//
// `PUT /api/v1/properties/{id}` carries the three blocks and NOT the ownership — quotas are
// validity-dated history with their own verb (PortfolioRequests.cs), so an address typo can
// never rewrite them.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@/components/layout/route-guards';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { PropertyForm } from '@/features/properties/PropertyForm';
import { useProperty, useUpdateProperty } from '@/features/properties/queries';
import { toPropertyForm, toPropertyRequest, type PropertyFormValues } from '@/features/properties/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/properties/$propertyId/edit')({
  component: EditPropertyScreen,
});

function EditPropertyScreen() {
  const { propertyId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const property = useProperty(propertyId);
  const update = useUpdateProperty();

  if (property.isPending) return <LoadingState rows={4} />;
  if (property.isError) return <ErrorState error={property.error} onRetry={() => void property.refetch()} />;

  const backToDetail = () => {
    void navigate({ to: '/app/properties/$propertyId', params: { propertyId } });
  };

  return (
    <RequirePermission permission="Portfolio.Write">
      <PropertyForm
        initialValues={toPropertyForm(property.data)}
        submitLabel={t('common.save')}
        onSubmit={async (values: PropertyFormValues) => {
          await update.mutateAsync({ path: { id: propertyId }, body: toPropertyRequest(values) });
          toast.success(t('properties.form.saved'));
          backToDetail();
        }}
        onCancel={backToDetail}
      />
    </RequirePermission>
  );
}
